import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { DollarBillIcon } from "@/components/DollarBillIcon";

export const LandingHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { label: "Home", href: "/" },
    { label: "How It Works", href: "/how-it-works" },
    { label: "Features", href: "/features" },
    { label: "About", href: "/about" },
    { label: "Contact", href: "/contact" },
  ];

  return (
    <motion.header className="fixed top-0 left-0 right-0 z-50 w-full">
      <div
        className={`bg-white/95 backdrop-blur-md border-b transition-all duration-300 ${
          isScrolled
            ? "border-fog shadow-[0_1px_12px_rgba(0,0,0,0.08)]"
            : "border-transparent"
        }`}
        style={{ height: 80 }}
      >
        <div className="container mx-auto max-w-7xl h-full px-6 flex items-center justify-between">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <Link to="/" className="flex items-center gap-2.5">
              <DollarBillIcon size={36} />
              <span className="text-xl font-bold tracking-tight">
                <span className="text-midnight">Dollar</span>{" "}
                <span className="text-vault-green">Bill</span>
              </span>
            </Link>
          </motion.div>

          <nav className="hidden md:flex items-center gap-8">
            {navItems.map((item, index) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link
                  to={item.href}
                  className={`text-sm font-medium transition-colors relative group ${
                    location.pathname === item.href
                      ? "text-midnight"
                      : "text-slate-brand hover:text-midnight"
                  }`}
                >
                  {item.label}
                  <span
                    className={`absolute -bottom-1 left-0 h-0.5 bg-vault-green transition-all ${
                      location.pathname === item.href
                        ? "w-full"
                        : "w-0 group-hover:w-full"
                    }`}
                  />
                </Link>
              </motion.div>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate("/auth")}
              className="font-medium text-slate-brand hover:text-midnight hover:bg-stone"
            >
              Sign In
            </Button>
            <Button
              onClick={() => navigate("/contact")}
              className="bg-vault-green hover:bg-vault-green/90 text-white px-6 rounded-lg font-medium"
            >
              Get Started
            </Button>
          </div>

          <button
            className="md:hidden p-2 text-midnight"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t border-fog px-6 py-4"
          >
            <div className="flex flex-col gap-4">
              {navItems.map((item) => (
                <Link
                  key={item.label}
                  to={item.href}
                  className={`text-sm font-medium transition-colors ${
                    location.pathname === item.href
                      ? "text-midnight"
                      : "text-slate-brand hover:text-midnight"
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.label}
                </Link>
              ))}
              <Button
                variant="ghost"
                onClick={() => navigate("/auth")}
                className="w-full text-slate-brand hover:text-midnight hover:bg-stone"
              >
                Sign In
              </Button>
              <Button
                onClick={() => navigate("/contact")}
                className="w-full bg-vault-green hover:bg-vault-green/90 text-white rounded-lg"
              >
                Get Started
              </Button>
            </div>
          </motion.div>
        )}
      </div>
    </motion.header>
  );
};
